import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';

// Base API URL — overridden via REACT_APP_API_URL (e.g. http://localhost:5000 in dev,
// https://api.trefim.to in prod). The BE expects same-eTLD+1 deploys for the
// refresh-cookie SameSite=Lax flow; cross-origin dev works because withCredentials
// is enabled and the BE CORS policy whitelists localhost:3000 with AllowCredentials.
const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

export const apiClient = axios.create({
  baseURL: BASE_URL,
  timeout: 15000,
  // HttpOnly refresh-token cookie is set on POST /api/v1/auth/login and
  // /api/v1/auth/external/{provider}/callback; auto-attached on /api/v1/auth/refresh.
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// In-memory access token store. We deliberately keep the access token out of
// localStorage — that way an XSS payload can't pull it. Persistence is the
// HttpOnly refresh cookie's job. AuthContext mirrors what's needed for the UI.
let accessToken: string | null = null;
export const setAccessToken = (token: string | null) => { accessToken = token; };
export const getAccessToken = () => accessToken;

// ─── Request: attach Authorization header ───────────────────────────────────
apiClient.interceptors.request.use((config) => {
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  return config;
});

// ─── Response: silent refresh-token retry on 401 ────────────────────────────
//
// On a 401 we POST /api/v1/auth/refresh once (the HttpOnly cookie carries the
// refresh token) and replay the original request with the rotated access token.
// If the refresh itself 401s, we treat the session as dead — clear local state,
// route to /login. A request-id-tagged _retry flag prevents infinite loops on
// endpoints that legitimately return 401 (e.g. wrong-password on /auth/login).

interface RetriableConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

let refreshPromise: Promise<string | null> | null = null;
const onRefreshFailure: Array<() => void> = [];

export const onSessionExpired = (handler: () => void) => {
  onRefreshFailure.push(handler);
};

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as RetriableConfig | undefined;
    const status = error.response?.status;

    // Don't refresh on auth endpoints themselves — they are the source of truth.
    const url = original?.url ?? '';
    const isAuthPath = url.includes('/api/v1/auth/');

    if (status === 401 && original && !original._retry && !isAuthPath) {
      original._retry = true;

      if (!refreshPromise) {
        refreshPromise = axios
          .post(`${BASE_URL}/api/v1/auth/refresh`, {}, { withCredentials: true })
          .then((res) => {
            const newToken = res.data?.accessToken as string | undefined;
            if (newToken) setAccessToken(newToken);
            return newToken ?? null;
          })
          .catch(() => null)
          .finally(() => { refreshPromise = null; });
      }

      const newToken = await refreshPromise;
      if (newToken) {
        original.headers.Authorization = `Bearer ${newToken}`;
        return apiClient(original);
      }

      // Refresh failed — session is gone.
      setAccessToken(null);
      onRefreshFailure.forEach((handler) => handler());
    }

    return Promise.reject(error);
  },
);

export default apiClient;
